import {
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { observer } from "mobx-react-lite";
import { useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { AssigneeAvatar } from "@/domains/pm/components/shared";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MentionUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url?: string | null;
  color?: string | null;
}

export interface MentionInsertion {
  userId: number;
  /** Display name as inserted into the body (used to detect deletion). */
  displayName: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  /** Fires when a user is picked from the @-popover. */
  onMentionAdd: (mention: MentionInsertion) => void;
  onSubmit: () => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  /**
   * WHA-3507: scope @-mention suggestions to users with access to this
   * issue's project. Required — passing 0 produces an empty suggestion
   * list (matching what RLS would surface to a viewer with no access).
   */
  projectId: number;
  /**
   * An explicit audience, when the surface is not a project (document comment
   * threads take theirs from `get_document_assignable_users`). When given,
   * `projectId` is not consulted and nothing is fetched.
   */
  users?: MentionUser[];
  /**
   * Extra classes applied to BOTH the textarea and its highlight overlay so
   * the two stay metric-synced. The composer uses this to drop the box's own
   * border/ring and blend it into a surrounding container (WHA-3644).
   */
  className?: string;
  /**
   * WHA-3810: Enter sends and Shift+Enter inserts a newline (chat-style),
   * used by the issue panel's pinned comment input and the building message
   * board's composer. Off by default so the inline comment editor keeps Enter
   * as a plain newline.
   */
  submitOnEnter?: boolean;
  /** Imperative handle — lets the composer's "@" button open the picker. */
  handleRef?: React.Ref<MentionTextareaHandle>;
  /**
   * False turns `@` into plain text: no member picker, no highlight. For a
   * surface with no mention notifications behind it (brief appendices), where a
   * highlighted name would promise someone is told.
   */
  mentions?: boolean;
}

export interface MentionTextareaHandle {
  /** Insert an "@" at the caret and open the member picker. */
  openMentionPicker: () => void;
  /** Focus the textarea and put the caret at the end of its text. */
  focusEnd: () => void;
}

function displayName(u: MentionUser): string {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name || u.email;
}

/**
 * Highlight `@<DisplayName>` substrings in `text` as bold red spans, using
 * `names` as the allow-list (only real org users get highlighted). Mirrors
 * `renderCommentBody` in IssueComments so the in-editor preview matches the
 * sent comment (WHA-3326).
 */
function renderHighlight(text: string, names: string[]): ReactNode {
  if (names.length === 0) return text;
  const escaped = [...names]
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(?:${escaped.join("|")})`, "g");
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    // WHA-3644: red but NOT bold. The overlay must lay text out identically
    // to the (plain-weight) textarea so the caret tracks the highlight; with
    // a proportional font a bold mention would be wider and drift the caret.
    parts.push(
      <span key={`m-${i++}`} className="text-red-600">
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

/**
 * Returns the mention query at the caret if any: `@foo` with no spaces yet,
 * preceded by start-of-string or whitespace. Null otherwise.
 */
function detectMention(text: string, caret: number): { start: number; query: string } | null {
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === "@") {
      if (i === 0 || /\s/.test(text[i - 1])) {
        return { start: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

export const MentionTextarea = observer(function MentionTextarea({
  value,
  onChange,
  onMentionAdd,
  onSubmit,
  placeholder,
  rows = 3,
  disabled,
  projectId,
  users,
  className,
  submitOnEnter = false,
  handleRef,
  mentions = true,
}: MentionTextareaProps) {
  const projectMembers = useProjectMembersStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [activeIndex, setActiveIndex] = useState(0);

  // WHA-3507: populate the project's member list. The store dedupes
  // repeat calls and caches across composer mounts, so re-rendering an
  // edit-mode textarea or remounting the new-comment composer is cheap.
  useEffect(() => {
    if (!users && projectId && mentions) {
      projectMembers.fetch(projectId);
    }
  }, [projectId, projectMembers, users, mentions]);

  const projectUsers = projectMembers.get(projectId);
  const allUsers = users ?? projectUsers;

  const mentionNames = useMemo(
    () => (mentions ? allUsers.map((u) => displayName(u)).filter((n) => n.length > 0) : []),
    [allUsers, mentions]
  );

  const filtered = useMemo<MentionUser[]>(() => {
    const q = query.toLowerCase().trim();
    const sorted = [...allUsers].sort((a, b) => displayName(a).localeCompare(displayName(b)));
    // Cap generously; the popover is scrollable (max-h-48 overflow-y-auto), so a
    // low cap was silently hiding valid project members on larger teams.
    const CAP = 50;
    if (!q) return sorted.slice(0, CAP);
    return sorted
      .filter((u) => {
        const n = displayName(u).toLowerCase();
        return n.includes(q) || u.email.toLowerCase().includes(q);
      })
      .slice(0, CAP);
  }, [allUsers, query]);

  // Reset active index when filter set changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query, popoverOpen]);
  // The list can also shrink under an unchanged query (the member list
  // loading or refreshing), so never point past its end.
  const highlighted = Math.min(activeIndex, Math.max(filtered.length - 1, 0));
  // Keep the clamp, so a list that grows back does not jump the highlight to
  // an index nobody chose.
  useEffect(() => {
    if (activeIndex !== highlighted) setActiveIndex(highlighted);
  }, [activeIndex, highlighted]);

  // The list scrolls (max-h-48); keep the highlighted person on screen.
  useEffect(() => {
    if (!popoverOpen) return;
    document.getElementById(`${listId}-${highlighted}`)?.scrollIntoView?.({ block: "nearest" });
  }, [highlighted, listId, popoverOpen]);

  // WHA-3454: auto-grow the textarea with its content. Reset to `auto`
  // first so it can also shrink, then size to the content. `max-h` caps
  // the growth. The highlight overlay is `inset-0`, so it tracks the new
  // height automatically.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    // The box is `border-box` (Tailwind preflight) but `scrollHeight` covers
    // only content + padding, so assigning it verbatim leaves the box a border
    // short and clips the text by that much — which reads as the composer's
    // input sitting off the row's centre line next to the h-8 buttons.
    const cs = window.getComputedStyle(ta);
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    ta.style.height = `${ta.scrollHeight + border}px`;
    // Only show the scrollbar (and its buttons) once the content actually
    // exceeds the max height (15rem = 240px). Comparing against the cap — not
    // clientHeight — avoids a phantom scrollbar from border/box-sizing rounding
    // on an empty or short box.
    ta.style.overflowY = ta.scrollHeight > 240 ? "auto" : "hidden";
  }, [value]);

  const closeMention = () => {
    setPopoverOpen(false);
    setQuery("");
    setMentionStart(-1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const caret = e.target.selectionStart ?? newValue.length;
    onChange(newValue);
    const mention = mentions ? detectMention(newValue, caret) : null;
    if (mention) {
      setPopoverOpen(true);
      setQuery(mention.query);
      setMentionStart(mention.start);
    } else {
      closeMention();
    }
  };

  const insertUser = (user: MentionUser) => {
    const textarea = textareaRef.current;
    if (!textarea || mentionStart < 0) return;
    const caret = textarea.selectionStart ?? value.length;
    const before = value.slice(0, mentionStart);
    const after = value.slice(caret);
    const insert = `@${displayName(user)} `;
    const next = before + insert + after;
    onChange(next);
    onMentionAdd({ userId: user.id, displayName: displayName(user) });
    closeMention();
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const newCaret = before.length + insert.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  // WHA-3810: the composer's "@" button inserts the trigger character at the
  // caret and opens the picker, so mentioning never requires typing.
  useImperativeHandle(handleRef, () => ({
    openMentionPicker: () => {
      const ta = textareaRef.current;
      const caret = ta?.selectionStart ?? value.length;
      // Keep `@` a mention trigger: it only counts at the start of a word.
      const prefix = caret > 0 && !/\s/.test(value[caret - 1]) ? " " : "";
      const before = value.slice(0, caret) + prefix + "@";
      onChange(before + value.slice(caret));
      setMentionStart(before.length - 1);
      setQuery("");
      setPopoverOpen(true);
      requestAnimationFrame(() => {
        ta?.focus();
        ta?.setSelectionRange(before.length, before.length);
      });
    },
    // WHA-3810: "Reply" prefills the composer, then hands it the caret.
    focusEnd: () => {
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
        ta.scrollIntoView({ block: "nearest" });
      });
    },
  }));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME (CJK etc.): the Enter that commits a composition must not pick a
    // mention or send the comment. keyCode 229 covers Safari, which fires the
    // confirming keydown with isComposing already false.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (popoverOpen && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(Math.min(i, filtered.length - 1) + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(Math.min(i, filtered.length - 1) - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertUser(filtered[highlighted]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
        return;
      }
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      onSubmit();
      return;
    }
    // Chat-style send: Enter submits, Shift+Enter keeps the newline.
    if (submitOnEnter && e.key === "Enter" && !e.shiftKey) {
      // An unresolved @query with zero matches (popoverOpen but nothing
      // rendered — e.g. a typo'd name): don't send mid-mention. Fall through
      // to the default newline, which also ends the query. Ctrl/Cmd+Enter
      // above stays an explicit send.
      if (popoverOpen) return;
      e.preventDefault();
      onSubmit();
    }
  };

  // Shared box metrics so the overlay aligns pixel-perfectly with the
  // textarea text. Any change here must be applied to BOTH elements or
  // wrapping will desync and the highlight will float off the words.
  // WHA-3644: switched from font-mono to the app's default sans so the
  // composer matches the chatbot. Alignment no longer depends on monospace
  // because the overlay now renders mentions in the SAME weight as the
  // textarea (red, not bold) — see renderHighlight — so glyph widths match
  // in a proportional font too.
  const boxClasses = "w-full rounded-md border px-3 py-2 text-sm leading-5";

  const listOpen = popoverOpen && filtered.length > 0;

  return (
    <Popover
      open={listOpen}
      onOpenChange={(open) => {
        if (!open) closeMention();
      }}
    >
      <PopoverAnchor asChild>
        <div ref={wrapperRef} className="relative">
          {/* Highlight overlay sits behind the textarea. Trailing zero-width
           * space preserves the height of an empty last line. */}
          <div
            aria-hidden
            className={cn(
              boxClasses,
              "pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words border-transparent text-foreground",
              className
            )}
            ref={overlayRef}
          >
            {renderHighlight(value, mentionNames)}
            {"​"}
          </div>
          <textarea
            ref={textareaRef}
            // Focus stays here while the arrow keys move the active option, so
            // the option is announced through aria-activedescendant. (A
            // textarea may not take role="combobox".)
            aria-controls={listOpen ? listId : undefined}
            aria-activedescendant={listOpen ? `${listId}-${highlighted}` : undefined}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onScroll={(e) => {
              if (overlayRef.current) {
                overlayRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
              }
            }}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            className={cn(
              boxClasses,
              "relative max-h-[15rem] resize-none overflow-y-hidden bg-transparent text-transparent caret-foreground selection:bg-primary/30 selection:text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring",
              className
            )}
          />
        </div>
      </PopoverAnchor>
      {/* Portalled, so a composer near the top of a scrolling panel or a
       * dialog does not clip the list. Focus stays in the textarea, which
       * drives the list with the arrow keys. */}
      <PopoverContent
        side="top"
        align="start"
        sideOffset={4}
        className="max-h-48 w-64 overflow-y-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Clicking back into the composer keeps typing the mention.
          if (wrapperRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        {/* The list is an inner element, so the popover keeps the id Radix
         * gave it and its own role. */}
        <div id={listId} role="listbox" data-composer-list="">
          {filtered.map((u, i) => (
            <button
              key={u.id}
              id={`${listId}-${i}`}
              type="button"
              role="option"
              aria-selected={i === highlighted}
              tabIndex={-1}
              onMouseDown={(e) => {
                // mousedown so we win over the textarea blur
                e.preventDefault();
                insertUser(u);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/50",
                i === highlighted && "bg-accent"
              )}
            >
              <AssigneeAvatar user={u} size={18} />
              <span className="truncate">{displayName(u)}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});
